import { Dialog } from 'ui';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, InputRadio, TextArea } from 'ui';
import ChatCheckbox from '../../atoms/ChatCheckbox';
import './ReportModal.scss';
import useReportModal from './useReportModal';

/**
 * ReportModal - Reusable modal for reporting issues with selectable options
 * Supports both single selection (radio-style) and multiple selection (checkbox) modes
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSubmit - Callback when report is submitted with { selectedOptions, comment }
 * @param {string} props.title - Modal title
 * @param {string} props.subtitle - Optional subtitle text
 * @param {Array} props.options - Array of { id, label } options to display
 * @param {string} props.selectionMode - 'single' or 'multiple' selection mode
 * @param {boolean} props.showTextarea - Whether to show the comment textarea
 * @param {string} props.textareaPlaceholder - Placeholder for the textarea
 * @param {string} props.submitButtonText - Text for the submit button
 * @param {string} props.cancelButtonText - Text for the cancel button
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting
 * @returns {JSX.Element} Rendered component
 */
function ReportModal({
  cancelButtonText,
  isSubmitting = false,
  onClose,
  onSubmit,
  open,
  options,
  selectionMode = 'single',
  showTextarea = true,
  submitButtonText,
  subtitle,
  textareaPlaceholder,
  title,
}) {
  const { t } = useTranslation(['chatbot', 'actions']);

  const { comment, handleClose, handleCommentChange, handleOptionToggle, handleSubmit, isOptionSelected, isSubmitDisabled } = useReportModal({
    selectionMode,
    onSubmit,
    onClose,
  });

  const displayOptions = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        checked: isOptionSelected(option.id),
      })),
    [options, isOptionSelected]
  );

  const finalCancelText = cancelButtonText || t('actions:Cancel');
  const finalSubmitText = isSubmitting ? t('chatbot:Sending...') : submitButtonText || t('actions:Send');

  return (
    <Dialog
      buttonTextCancel={finalCancelText}
      buttonTextSuccess={finalSubmitText}
      className="report-modal"
      isDisabledSuccess={isSubmitDisabled || isSubmitting}
      isOpen={open}
      onCancel={handleClose}
      onSuccess={handleSubmit}
      hasSeparator
      size="m"
      title={title}
    >
      <div className="report-modal__content">
        {subtitle && <p className="report-modal__subtitle">{subtitle}</p>}

        <div className="report-modal__options">
          {displayOptions.map((option) =>
            selectionMode === 'single' ? (
              <InputRadio
                checked={option.checked}
                key={option.id}
                label={option.label}
                name="report-option"
                onChange={() => handleOptionToggle(option.id)}
                value={option.id}
              />
            ) : (
              <ChatCheckbox
                checked={option.checked}
                checkboxPosition="right"
                className="report-modal__option"
                id={`report-option-${option.id}`}
                key={option.id}
                label={option.label}
                name={option.id}
                onChange={() => handleOptionToggle(option.id)}
              />
            )
          )}
        </div>

        {showTextarea && (
          <div className="report-modal__comment">
            <FormField isDisabled={isSubmitting} isLabelHidden label={textareaPlaceholder || t('chatbot:flag_comment_placeholder')}>
              <TextArea
                onChange={(e) => handleCommentChange(e.target.value)}
                placeholder={textareaPlaceholder || t('chatbot:flag_comment_placeholder')}
                rows={3}
                value={comment}
              />
            </FormField>
          </div>
        )}
      </div>
    </Dialog>
  );
}

ReportModal.propTypes = {
  cancelButtonText: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectionMode: PropTypes.oneOf(['single', 'multiple']),
  showTextarea: PropTypes.bool,
  submitButtonText: PropTypes.string,
  subtitle: PropTypes.string,
  textareaPlaceholder: PropTypes.string,
  title: PropTypes.string.isRequired,
};

export default ReportModal;
